import React from 'react';
import Avatar from '@mui/material/Avatar';
import { API_URL } from '../api/config';
import { getDefaultAvatarUrl } from '../utils/avatarHelpers';

/**
 * Avatar component with automatic fallback to default image on error
 * Handles external images (Google, etc.) differently to avoid ORB blocking
 *
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for the image
 * @param {object} sx - MUI sx prop for styling
 * @param {number} size - Avatar size (width and height)
 */
const AvatarWithFallback = React.memo(({ src, alt, sx, size }) => {
  const [imgSrc, setImgSrc] = React.useState(src);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = React.useCallback(() => {
    if (!hasError) {
      console.log('Avatar failed to load, using default:', imgSrc);
      setImgSrc(getDefaultAvatarUrl());
      setHasError(true);
    }
  }, [hasError, imgSrc]);

  // Check if the image is from an external source (Google, etc.)
  const isExternalImage = imgSrc && (imgSrc.startsWith('http://') || imgSrc.startsWith('https://')) && !imgSrc.includes(API_URL);

  // For external images (like Google), use minimal props to avoid blocking
  // Don't set referrerPolicy or crossOrigin for external images - let browser handle it naturally
  // This matches the working implementation in courses-cursor project
  const imgProps = isExternalImage ? {
    loading: 'lazy',
    onError: handleError,
  } : {
    crossOrigin: 'anonymous',
    loading: 'lazy',
    onError: handleError,
  };

  return (
    <Avatar
      src={imgSrc}
      alt={alt}
      slotProps={{
        img: imgProps,
      }}
      sx={{
        ...sx,
        width: size || sx?.width,
        height: size || sx?.height,
      }}
    />
  );
});

AvatarWithFallback.displayName = 'AvatarWithFallback';

export default AvatarWithFallback;
